import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Trash2, Copy, CheckCircle, AlertTriangle, Calculator } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { countDataverseRecords } from '@/lib/api';
import { FlexibleFilter, FilterType, StaticValueCondition, CrossEntityCondition, MockEntity } from '../flexible/flexible-filter-types';
import { FilterBuilderProps, FilterBuilderState, FilterBuilderEntity } from './filter-builder-types';
import { StaticValueCondition as StaticValueConditionComponent } from '../flexible/StaticValueCondition';
import { CrossEntityCondition as CrossEntityConditionComponent } from '../flexible/CrossEntityCondition';
import { FilterObject, FilterCondition } from '@shared/filter-types';

// Helper function to check if an operator requires a value
const operatorRequiresValue = (operator: string): boolean => {
  const nullOperators = ['null', 'notnull', 'containsdata', 'doesnotcontaindata'];
  return !nullOperators.includes(operator.toLowerCase());
};

// Utility function to generate primary ID field from entity name
const getPrimaryIdField = (entityName: string): string => {
  return `${entityName}id`;
};

// Convert FlexibleFilter to FilterObject format
const convertFiltersToFilterObject = (filters: FlexibleFilter[]): { entityName: string; filterObject: FilterObject } => {
  const conditions: FilterCondition[] = [];
  let targetEntity = '';

  for (const filter of filters) {
    if (filter.type === 'static') {
      const staticCond = filter.condition as StaticValueCondition;
      targetEntity = staticCond.entity;

      // Ensure we have valid values for the API
      let conditionValue = staticCond.value;
      if (conditionValue === null || conditionValue === undefined) {
        conditionValue = '';
      }

      conditions.push({
        field: staticCond.field,
        operator: staticCond.operator,
        value: conditionValue
      } as any); // Type assertion for compatibility
    } else if (filter.type === 'cross-entity') {
      const crossCond = filter.condition as CrossEntityCondition;
      // For cross-entity filters in association mode, count on parent entity (source)
      // For other modes, count records in the child entity
      targetEntity = crossCond.parentEntity;

      // Add parent conditions to the filter (these are conditions on the source entity)
      if (crossCond.parentConditions && Array.isArray(crossCond.parentConditions)) {
        for (const parentCondition of crossCond.parentConditions) {
          let conditionValue = parentCondition.value;
          if (conditionValue === null || conditionValue === undefined) {
            conditionValue = '';
          }

          conditions.push({
            field: parentCondition.field,
            operator: parentCondition.operator,
            value: conditionValue
          } as any); // Type assertion for compatibility
        }
      }
    }
  }

  // If no conditions, add a "count all" condition (primary field is not null)
  // This ensures we have at least one condition for the API
  if (conditions.length === 0 && targetEntity) {
    conditions.push({
      field: getPrimaryIdField(targetEntity),
      operator: 'containsdata',
      value: '' // Use empty string instead of null for frontend compatibility
    } as any); // Type assertion to handle the null/empty string mismatch
  }

  return {
    entityName: targetEntity,
    filterObject: { and: conditions }
  };
};

export const ReusableFilterBuilder: React.FC<FilterBuilderProps> = ({
  context,
  initialFilters = [],
  onFiltersChange,
  onValidationChange,
  onRecordCountChange,
  className,
  disabled = false,
  title = 'Filter Builder',
  description = 'Create custom filters for your data',
  forceFilterType,
  filterLocation,
  parentEntity,
  childEntity,
  associationConfig,
  modifiedDateFilter,
  _recordCount,
  recordCountEntity,
  isCountingRecords: isCountingRecordsProp,
  onCountRecords
}) => {
  const { toast } = useToast();
  const [state, setState] = useState<FilterBuilderState>({
    filters: initialFilters,
    entities: [],
    loading: false,
    error: null,
    isValid: true
  });

  const [nextId, setNextId] = useState(1);
  // Use prop if provided, otherwise use local state for backwards compatibility
  const [isCountingRecordsLocal, setIsCountingRecords] = useState(false);
  const isCountingRecords = isCountingRecordsProp ?? isCountingRecordsLocal;

  // Track last emitted filters to prevent infinite loops
  const lastEmittedFiltersRef = React.useRef<string>(JSON.stringify(initialFilters));
  const isSyncingFromParentRef = React.useRef(false);

  // Load entities on mount
  useEffect(() => {
    loadEntities();
  }, []);

  // Sync filters with parent component - only if they actually changed from what we last emitted
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(state.filters);

    // Skip if we're currently syncing FROM the parent (to prevent echo)
    if (isSyncingFromParentRef.current) {
      isSyncingFromParentRef.current = false;
      return;
    }

    // Only call onChange if filters actually changed from what we last emitted
    if (currentFiltersStr !== lastEmittedFiltersRef.current) {
      lastEmittedFiltersRef.current = currentFiltersStr;
      onFiltersChange(state.filters);
    }

    if (onValidationChange) {
      onValidationChange(state.isValid);
    }
  }, [state.filters, state.isValid, onFiltersChange, onValidationChange]);

  // Sync state when initialFilters prop changes (with deep equality check to prevent infinite loops)
  useEffect(() => {
    const incomingFiltersStr = JSON.stringify(initialFilters);

    // Only update if the content has actually changed from current state
    const filtersChanged = JSON.stringify(state.filters) !== incomingFiltersStr;
    if (filtersChanged) {
      // Set flag to prevent echo back to parent
      isSyncingFromParentRef.current = true;
      lastEmittedFiltersRef.current = incomingFiltersStr;

      setState(prev => ({ ...prev, filters: initialFilters }));
    }
  }, [initialFilters]);

  const loadEntities = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const entities = await context.entityProvider();
      const enhancedEntities: FilterBuilderEntity[] = entities.map(entity => ({
        ...entity,
        fields: [],
        lookupFields: [],
        relatedEntities: []
      }));

      setState(prev => ({ 
        ...prev, 
        entities: enhancedEntities, 
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to load entities', 
        loading: false 
      }));
    }
  }, [context]);

  const addFilter = useCallback(() => {
    // Check if we're in a parent/child context (Direct Sync mapping)
    const hasParentChildContext = context.contextParentEntity && context.contextChildEntity;

    // Determine filter type - use forceFilterType if provided, otherwise default to 'static'
    const filterType: FilterType = forceFilterType || 'static';

    let newFilter: FlexibleFilter;

    if (filterType === 'cross-entity') {
      // Create cross-entity filter
      newFilter = {
        id: `filter_${nextId}`,
        type: 'cross-entity',
        condition: {
          type: 'cross-entity',
          id: `condition_${nextId}`,
          parentEntity: context.contextParentEntity || '',
          childEntity: context.contextChildEntity || '',
          parentLookupField: '',
          childLookupField: '',
          childConditions: [],
          parentConditions: []
        } as CrossEntityCondition
      };
    } else {
      // Create static filter (default)
      newFilter = {
        id: `filter_${nextId}`,
        type: 'static',
        condition: {
          type: 'static',
          id: `condition_${nextId}`,
          entity: hasParentChildContext ? context.contextParentEntity : '',
          field: '',
          operator: 'eq', // Set default operator to prevent undefined errors
          value: '',
          // Set default targetEntity to 'parent' when in parent/child context
          ...(hasParentChildContext && { targetEntity: 'parent' })
        } as StaticValueCondition
      };
    }

    console.log('[ReusableFilterBuilder] Creating new filter:', {
      hasParentChildContext,
      parentEntity: context.contextParentEntity,
      childEntity: context.contextChildEntity,
      newFilter
    });

    setState(prev => ({
      ...prev,
      filters: [...prev.filters, newFilter]
    }));
    setNextId(nextId + 1);
  }, [nextId, context.contextParentEntity, context.contextChildEntity, forceFilterType]);

  const updateFilter = useCallback((id: string, updates: Partial<FlexibleFilter>) => {
    setState(prev => ({
      ...prev,
      filters: prev.filters.map(f => f.id === id ? { ...f, ...updates } : f)
    }));
  }, []);

  const removeFilter = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      filters: prev.filters.filter(f => f.id !== id)
    }));
  }, []);

  // Helper function to deep clone conditions with new IDs
  const cloneCondition = (condition: StaticValueCondition | CrossEntityCondition, baseId: string) => {
    if (condition.type === 'static') {
      return {
        ...condition,
        id: `condition_${baseId}`
      } as StaticValueCondition;
    } else if (condition.type === 'cross-entity') {
      return {
        ...condition,
        id: `condition_${baseId}`,
        childConditions: condition.childConditions.map((childCond, index) => ({
          ...childCond,
          id: `child_condition_${baseId}_${index}`
        })),
        parentConditions: condition.parentConditions?.map((parentCond, index) => ({
          ...parentCond,
          id: `parent_condition_${baseId}_${index}`
        })) || []
      } as CrossEntityCondition;
    }
    return condition;
  };

  const cloneFilter = useCallback((id: string) => {
    setState(prev => {
      const filterToClone = prev.filters.find(f => f.id === id);
      if (!filterToClone) return prev;

      // Generate unique IDs using timestamp and random values to avoid conflicts
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 9);
      const uniqueId = `${timestamp}_${random}`;

      // Deep clone the filter with new unique IDs
      const clonedFilter: FlexibleFilter = {
        ...filterToClone,
        id: `filter_${uniqueId}`,
        condition: cloneCondition(filterToClone.condition, uniqueId)
      };

      return {
        ...prev,
        filters: [...prev.filters, clonedFilter]
      };
    });
  }, []);

  const validateFilters = useCallback(() => {
    const validation = state.filters.map(filter => {
      const isValid = validateSingleFilter(filter);
      return { id: filter.id, isValid, issues: getValidationIssues(filter) };
    });

    const overallValid = validation.every(v => v.isValid);
    setState(prev => ({ ...prev, isValid: overallValid }));

    // Show validation feedback to user
    const invalidCount = validation.filter(v => !v.isValid).length;
    const totalCount = validation.length;

    if (totalCount === 0) {
      toast({
        title: "No Filters",
        description: "Add some filters first to validate them.",
        variant: "default"
      });
    } else if (overallValid) {
      toast({
        title: "Validation Successful",
        description: `All ${totalCount} filter(s) are valid and ready to use.`,
        variant: "default"
      });
    } else {
      const issues = validation
        .filter(v => !v.isValid)
        .map(v => v.issues.join(', '))
        .join('; ');

      toast({
        title: "Validation Failed",
        description: `${invalidCount} of ${totalCount} filter(s) have issues: ${issues}`,
        variant: "destructive"
      });
    }

    return validation;
  }, [state.filters, toast]);

  const validateSingleFilter = useCallback((filter: FlexibleFilter): boolean => {
    switch (filter.type) {
      case 'static': {
        const staticCond = filter.condition as StaticValueCondition;
        const requiredFields = staticCond.entity && staticCond.field && staticCond.operator;
        const valueRequired = operatorRequiresValue(staticCond.operator) ? !!staticCond.value : true;
        return !!(requiredFields && valueRequired);
      }
      case 'cross-entity': {
        const crossCond = filter.condition as CrossEntityCondition;
        const basicFieldsValid = !!(crossCond.parentEntity && crossCond.childEntity &&
                                   crossCond.parentLookupField && crossCond.childLookupField);
        
        // For entity-mappings context, child conditions are optional (backward compatibility)
        const childConditionsValid = context.contextType === 'entity-mappings'
          ? true
          : crossCond.childConditions.length > 0;
        
        return basicFieldsValid && childConditionsValid;
      }
      default:
        return false;
    }
  }, [context.contextType]);

  const getValidationIssues = useCallback((filter: FlexibleFilter): string[] => {
    const issues: string[] = [];
    
    switch (filter.type) {
      case 'static': {
        const staticCond = filter.condition as StaticValueCondition;
        if (!staticCond.entity) issues.push('Entity is required');
        if (!staticCond.field) issues.push('Field is required');
        if (!staticCond.operator) issues.push('Operator is required');
        // Only require value if the operator needs one
        if (operatorRequiresValue(staticCond.operator) && !staticCond.value) {
          issues.push('Value is required');
        }
        break;
      }
      case 'cross-entity': {
        const crossCond = filter.condition as CrossEntityCondition;
        if (!crossCond.parentEntity) issues.push('Parent entity is required');
        if (!crossCond.childEntity) issues.push('Child entity is required');
        if (!crossCond.parentLookupField) issues.push('Parent lookup field is required');
        if (!crossCond.childLookupField) issues.push('Child lookup field is required');
        
        // For entity-mappings context, child conditions are optional (backward compatibility)
        if (context.contextType !== 'entity-mappings' && !crossCond.childConditions.length) {
          issues.push('At least one child condition is required');
        }
        break;
      }
      default:
        break;
    }
    
    return issues;
  }, [context.contextType]);

  const exportFilters = useCallback(() => {
    const exportData = {
      filters: state.filters,
      context: context.contextType,
      timestamp: new Date().toISOString(),
      version: '1.0',
      metadata: {
        entityCount: state.entities.length,
        filterCount: state.filters.length,
        validationStatus: state.isValid
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `filters-${context.contextType}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state.filters, state.entities, state.isValid, context.contextType]);

  const countRecords = useCallback(async () => {
    console.log('[ReusableFilterBuilder] countRecords - modifiedDateFilter:', modifiedDateFilter);
    console.log('[ReusableFilterBuilder] onCountRecords:', onCountRecords, 'type:', typeof onCountRecords);

    // If parent provides onCountRecords callback, use that instead
    if (onCountRecords) {
      console.log('[ReusableFilterBuilder] Calling parent onCountRecords callback');
      onCountRecords();
      return;
    }

    console.log('[ReusableFilterBuilder] No onCountRecords callback, using internal logic');

    // Check if we have either filters OR a date filter enabled
    const hasFilters = state.filters.length > 0;
    const hasDateFilter = modifiedDateFilter?.enabled === true;

    if (!hasFilters && !hasDateFilter) {
      toast({
        title: "No Filters",
        description: "Add some filters or enable date filtering first to count records.",
        variant: "default"
      });
      return;
    }

    // Validate filters first (if we have any)
    if (hasFilters) {
      const validation = state.filters.map(filter => ({
        id: filter.id,
        isValid: validateSingleFilter(filter),
        issues: getValidationIssues(filter)
      }));

      const overallValid = validation.every(v => v.isValid);
      if (!overallValid) {
        const invalidCount = validation.filter(v => !v.isValid).length;
        toast({
          title: "Invalid Filters",
          description: `Please fix ${invalidCount} invalid filter(s) before counting records.`,
          variant: "destructive"
        });
        return;
      }
    }

    try {
      setIsCountingRecords(true);

      // Get entity name and filter object
      let entityName = '';
      let filterObject: any = { and: [] };

      if (hasFilters) {
        const converted = convertFiltersToFilterObject(state.filters);
        entityName = converted.entityName;
        filterObject = converted.filterObject;
      } else if (recordCountEntity) {
        // If no filters but we have a recordCountEntity (from parent), use that
        entityName = recordCountEntity;
      }

      if (!entityName) {
        toast({
          title: "No Entity Selected",
          description: "Please select an entity in your filters or mapping configuration.",
          variant: "destructive"
        });
        return;
      }

      const primaryIdField = getPrimaryIdField(entityName);
      console.log('[ReusableFilterBuilder] Calling API with:', { entityName, primaryIdField, filterObject, associationConfig, modifiedDateFilter });
      const count = await countDataverseRecords(entityName, primaryIdField, filterObject, associationConfig, modifiedDateFilter);

      // Handle null count (when counting is not supported for processing-stage operators)
      if (count === null) {
        toast({
          title: "Record Count Not Available",
          description: "This filter uses processing-stage operators that cannot be counted. The sync operation will work correctly.",
          variant: "default"
        });
        return;
      }

      // Update the parent component with the count
      if (onRecordCountChange) {
        onRecordCountChange(count, entityName);
      }

      toast({
        title: "Record Count",
        description: `Found ${count.toLocaleString()} record(s) matching your filters in ${entityName}.`,
        variant: "default"
      });

    } catch (error) {
      console.error('Error counting records:', error);
      const debugInfo = convertFiltersToFilterObject(state.filters);
      console.error('Filter object being sent:', debugInfo);

      // Clear the count on error
      if (onRecordCountChange) {
        onRecordCountChange(null);
      }

      toast({
        title: "Count Failed",
        description: error instanceof Error ? error.message : "Failed to count records.",
        variant: "destructive"
      });
    } finally {
      setIsCountingRecords(false);
    }
  }, [state.filters, validateSingleFilter, getValidationIssues, toast, onRecordCountChange, associationConfig, modifiedDateFilter]);

  // Convert FilterBuilderEntity to MockEntity for component compatibility
  const convertToMockEntity = useCallback((entity: FilterBuilderEntity): MockEntity => {
    return {
      LogicalName: entity.LogicalName || entity.logicalName || '',
      SchemaName: entity.SchemaName || entity.displayName || entity.LogicalName || entity.logicalName || '',
      fields: entity.fields?.map(field => ({
        name: field.logicalName || field.LogicalName || '',
        type: mapFieldTypeToMockType(field.type || field.AttributeType || 'string'),
        displayName: field.displayName || field.LogicalName || field.logicalName || ''
      })) || [],
      lookupFields: entity.lookupFields?.map(field => ({
        name: field.logicalName || field.LogicalName || '',
        displayName: field.displayName || field.LogicalName || field.logicalName || '',
        targetEntity: 'unknown' // Would need to be determined from field metadata
      })) || []
    };
  }, []);

  // Map DataverseFieldInfo type to MockField type
  const mapFieldTypeToMockType = (type: string): 'string' | 'number' | 'boolean' | 'date' => {
    const normalizedType = (type || '').toLowerCase();
    switch (normalizedType) {
      case 'number':
      case 'integer':
      case 'decimal':
      case 'double':
      case 'money':
        return 'number';
      case 'boolean':
        return 'boolean';
      case 'datetime':
      case 'date':
        return 'date';
      default:
        return 'string';
    }
  };

  const renderFilterCondition = useCallback((filter: FlexibleFilter) => {
    const mockEntities = state.entities.map(convertToMockEntity);
    
    switch (filter.type) {
      case 'static':
        return (
          <StaticValueConditionComponent
            condition={filter.condition as StaticValueCondition}
            entities={mockEntities}
            onChange={(condition) => updateFilter(filter.id, { condition })}
            parentEntity={parentEntity || context.contextParentEntity}
            childEntity={childEntity || context.contextChildEntity}
            filterLocation={filterLocation}
          />
        );
      case 'cross-entity':
        return (
          <CrossEntityConditionComponent
            condition={filter.condition as CrossEntityCondition}
            entities={mockEntities}
            onChange={(condition) => updateFilter(filter.id, { condition })}
            contextParentEntity={context.contextParentEntity}
            contextChildEntity={context.contextChildEntity}
          />
        );
      default:
        return <div>Unknown filter type</div>;
    }
  }, [state.entities, convertToMockEntity, updateFilter, context.contextParentEntity, context.contextChildEntity, parentEntity, childEntity, filterLocation]);

  if (state.loading) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
            <span>Loading entities...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state.error) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
          <Button type="button" onClick={loadEntities} className="mt-4" variant="outline">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={addFilter}
            disabled={disabled || !!(context.maxFilters && state.filters.length >= context.maxFilters)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Filter
          </Button>
          <Button type="button" variant="outline" onClick={validateFilters}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Validate
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={countRecords}
            disabled={isCountingRecords || (state.filters.length === 0 && !(modifiedDateFilter?.enabled))}
          >
            {isCountingRecords ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
            ) : (
              <Calculator className="h-4 w-4 mr-2" />
            )}
            {isCountingRecords ? 'Counting...' : 'Count Records'}
          </Button>
          {context.enableExport && (
            <Button type="button" variant="outline" onClick={exportFilters}>
              Export
            </Button>
          )}
        </div>
      </div>

      {state.filters.length === 0 && (
        <Card key="empty-state">
          <CardContent className="py-12">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">No filters defined</h3>
              <p className="text-muted-foreground mb-4">
                Click "Add Filter" to create your first filter condition
              </p>
              <Button onClick={addFilter} disabled={disabled}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Filter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {state.filters.map((filter, index) => (
        <Card key={filter.id} className="relative">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Filter {index + 1}</CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Label>Filter Type:</Label>
                  <Select
                    value={filter.type}
                    onValueChange={(type: FilterType) => {
                      let newCondition;
                      switch (type) {
                        case 'static':
                          newCondition = {
                            type: 'static' as const,
                            id: `condition_${nextId}`,
                            entity: '',
                            field: '',
                            operator: '',
                            value: ''
                          };
                          break;
                        case 'cross-entity':
                          newCondition = {
                            type: 'cross-entity' as const,
                            id: `condition_${nextId}`,
                            parentEntity: '',
                            childEntity: '',
                            parentLookupField: '',
                            childLookupField: '',
                            childConditions: [],
                            parentConditions: []
                          };
                          break;
                        default:
                          newCondition = filter.condition;
                      }
                      
                      updateFilter(filter.id, { type, condition: newCondition });
                      setNextId(nextId + 1);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="static">Static Value</SelectItem>
                      {context.enableCrossEntity && (
                        <SelectItem value="cross-entity">Cross-Entity</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => cloneFilter(filter.id)}
                  disabled={disabled}
                  className="text-muted-foreground hover:text-foreground"
                  title="Clone filter"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFilter(filter.id)}
                  disabled={disabled}
                  className="text-destructive"
                  title="Delete filter"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderFilterCondition(filter)}
          </CardContent>
        </Card>
      ))}

    </div>
  );
};